import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@goaltree/ui/components/popover'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation } from 'convex/react'
import i18next from 'i18next'
import {
  CalendarCheck,
  CalendarDays,
  Check,
  ChevronRight,
  Info,
  LoaderCircle,
  PencilLine,
  Plus,
  RefreshCw,
  Snowflake,
  Trash2,
} from 'lucide-react'
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AddNodeRow } from '@/components/add-node-row'
import { CoachEditedTag } from '@/components/coaching/coach-edited-tag'
import { goalFreezeState, useGoalFreezeActions } from '@/components/goal-freeze'
import { DeleteTaskDialog, GoalTaskMenu } from '@/components/goal-task-menu'
import { InlineEditor, type InlineEditorHandle } from '@/components/inline-editor'
import { LinkifiedText } from '@/components/linkified-text'
import type { NodeDetail } from '@/components/node-detail-sheet'
import { NodeDetailSheet } from '@/components/node-detail-sheet'
import {
  FoldedMilestone,
  MilestoneEyebrow,
  MilestonePill,
  MilestoneProgress,
  type MilestoneState,
} from '@/components/plan-outline'
import { useLongPressMenu } from '@/components/task-context-menu'
import { UrgentFlag } from '@/components/task-meta'
import { TaskStatusIcon } from '@/components/task-status-icon'
import { useAddNode } from '@/hooks/use-add-node'
import { useDeleteNode } from '@/hooks/use-delete-node'
import { useGoalReplanStatus } from '@/hooks/use-goal-replan-status'
import { useGoals } from '@/hooks/use-goals'
import { useMustDo } from '@/hooks/use-must-do'
import { useOnlineStatus } from '@/hooks/use-online-status'
import { useRenameNode } from '@/hooks/use-rename-node'
import { useSetTaskStatus } from '@/hooks/use-set-task-status'
import { useStickyHeightVar, useStuck } from '@/hooks/use-stuck'
import { celebrateFrom } from '@/lib/celebrate'
import { startHereGoalId } from '@/lib/first-run'
import { optimisticTaskStatus } from '@/lib/goals-optimistic'
import { haptic } from '@/lib/haptics'
import { completeNode, uncompleteNode } from '@/lib/nodes-optimistic'
import { periodLabel } from '@/lib/schedule'
import { playDoneSound } from '@/lib/sounds'
import { taskTimeLabel } from '@/lib/task-time'
import { undoToast } from '@/lib/undo-toast'
import { useOfflineStore } from '@/store/offline.store'

function useContextMenu() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<{ x: number; y: number } | null>(null)
  const preventCloseUntil = useRef(0)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressOccurred = useRef(false)
  const touchStartY = useRef<number | null>(null)

  function openAt(globalX: number, globalY: number) {
    setMenuAnchor({ x: globalX, y: globalY })
    setMenuOpen(true)
    preventCloseUntil.current = Date.now() + 600
  }

  function startLongPress(globalX: number, globalY: number) {
    if (menuOpen) return
    touchStartY.current = globalY
    longPressOccurred.current = false
    longPressTimer.current = setTimeout(() => {
      longPressOccurred.current = true
      openAt(globalX, globalY)
    }, 500)
  }

  function cancelLongPress() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  const longPressHandlers = {
    onMouseDown: (e: React.MouseEvent) => {
      if (e.button === 0) startLongPress(e.clientX, e.clientY)
    },
    onMouseMove: (e: React.MouseEvent) => {
      if (Math.abs(e.movementX) > 3 || Math.abs(e.clientY - (touchStartY.current ?? e.clientY)) > 3)
        cancelLongPress()
    },
    onMouseUp: cancelLongPress,
    onMouseLeave: cancelLongPress,
    onTouchStart: (e: React.TouchEvent) =>
      startLongPress(e.touches[0].clientX, e.touches[0].clientY),
    onTouchMove: (e: React.TouchEvent) => {
      if (Math.abs(e.touches[0].clientY - (touchStartY.current ?? 0)) > 8) cancelLongPress()
    },
    onTouchEnd: cancelLongPress,
  }

  function handleOpenChange(open: boolean) {
    if (!open && Date.now() < preventCloseUntil.current) return
    setMenuOpen(open)
    if (!open) setMenuAnchor(null)
  }

  return { menuOpen, menuAnchor, openAt, longPressHandlers, handleOpenChange, longPressOccurred }
}

// `n` bumps on every jump, so re-focusing the same node scrolls/highlights again.
// Same drag distance Everyday rows need to commit a swipe.
const SWIPE_THRESHOLD = 80
// Same timings as Everyday rows: slide off, then collapse.
const LEAVE_SLIDE_MS = 400
const LEAVE_COLLAPSE_MS = 300
// Matches Everyday's pause between the circle filling and the row moving.
const TAP_HOLD_MS = 800

const FocusNodeContext = createContext<{ id?: string; n: number }>({ n: 0 })
const StartHereContext = createContext<string | undefined>(undefined)

type GoalCtxValue = {
  goalId: Id<'nodes'>
  goalTitle: string
  category: string
  onView: (detail: NodeDetail) => void
  // Ticking a task on a frozen goal offers to unfreeze it (never blocks).
  onTickWhileFrozen?: () => void
  // The client's coach: edits the plan but can't tick tasks off.
  viewerIsCoach?: boolean
  // Must-do is the owner's own lane.
  isOwner?: boolean
}
const GoalCtx = createContext<GoalCtxValue | null>(null)

type RenameHandler = (id: Id<'nodes'>, title: string) => Promise<unknown>
type DeleteHandler = (id: Id<'nodes'>) => Promise<unknown>
type AddHandler = (args: {
  parentId: Id<'nodes'>
  type: 'milestone' | 'target' | 'task'
  title: string
  category: string
}) => Promise<unknown>

type TaskNode = {
  _id: Id<'nodes'>
  title: string
  description?: string
  effort: 'low' | 'medium' | 'high'
  superUrgent?: boolean
  createdAt?: number
  completedAt: number | null
  status: 'backlog' | 'in-progress' | 'done'
  dueDate?: number | null
  recurs?: boolean
  recursPeriod?: string
  recursDays?: number[]
  recurrenceDay?: number
  recurrenceTime?: string
  durationMinutes?: number
  allDay?: boolean
  mustDoOn?: string
  isHabit?: boolean
  lastEditedBy?: string
}

type TargetNode = {
  _id: Id<'nodes'>
  title: string
  progress: number
  tasks: TaskNode[]
}

type MilestoneNode = {
  _id: Id<'nodes'>
  title: string
  progress: number
  completedAt: number | null
  targets: TargetNode[]
}

export type GoalTreeData = {
  _id: Id<'nodes'>
  title: string
  category: string
  progress: number
  milestones: MilestoneNode[]
  viewerIsCoach?: boolean
  isOwner?: boolean
}

function TaskRow({
  task,
  rename,
  milestoneTitle,
  targetTitle,
  canEdit = true,
  anchor = true,
  context,
}: {
  task: TaskNode
  rename: RenameHandler
  milestoneTitle: string
  targetTitle: string
  canEdit?: boolean
  // False for the "Up next" copy — the real row owns the DOM ids that focus and
  // scroll-to-node look up.
  anchor?: boolean
  // Extra meta shown after the details (Up next names the milestone).
  context?: string
}) {
  const { t } = useTranslation()
  const complete = useMutation(api.nodes.complete).withOptimisticUpdate((store, { taskId }) =>
    completeNode(store, taskId),
  )
  const uncomplete = useMutation(api.nodes.uncomplete).withOptimisticUpdate((store, { taskId }) =>
    uncompleteNode(store, taskId),
  )
  const setTaskStatus = useMutation(api.nodes.setTaskStatus).withOptimisticUpdate(
    optimisticTaskStatus,
  )
  const online = useOnlineStatus()
  const enqueueOp = useOfflineStore((s) => s.enqueue)
  const goalCtx = useContext(GoalCtx)
  const startHereId = useContext(StartHereContext)
  const startHere = !anchor && startHereId === task._id
  const [optimisticDone, setOptimisticDone] = useState<boolean | null>(null)
  const [localStatus, setLocalStatus] = useState<'backlog' | 'in-progress'>(
    task.status === 'in-progress' ? 'in-progress' : 'backlog',
  )
  const focus = useContext(FocusNodeContext)
  const [highlighted, setHighlighted] = useState(false)
  const rowRef = useRef<HTMLLIElement>(null)
  const taskTitleRef = useRef<InlineEditorHandle>(null)
  const isTouch = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches

  useEffect(() => {
    // Only the row in the plan glows, not its "Up next" copy.
    if (!anchor || focus.id !== task._id) return
    // Start once the smooth scroll has landed; clear after the glow fades.
    setHighlighted(false)
    const t = setTimeout(() => setHighlighted(true), 700)
    const u = setTimeout(() => setHighlighted(false), 3600)
    return () => {
      clearTimeout(t)
      clearTimeout(u)
    }
  }, [focus.id, focus.n, task._id, anchor])

  const isDone = optimisticDone ?? task.completedAt !== null

  useEffect(() => {
    setOptimisticDone(null)
  }, [task.completedAt])

  // Re-sync from the query so external status changes (e.g. from the detail
  // sheet) reflect in the row, not just local long-press toggles.
  useEffect(() => {
    setLocalStatus(task.status === 'in-progress' ? 'in-progress' : 'backlog')
  }, [task.status])

  const isInProgress = localStatus === 'in-progress' && !isDone
  const canTick = canEdit && !goalCtx?.viewerIsCoach
  const ctxMenu = useLongPressMenu({ disabled: !canEdit })
  const [pendingDelete, setPendingDelete] = useState(false)
  const mustDo = useMustDo()
  const canMustDo = canEdit && !!goalCtx?.isOwner && !task.isHabit && !isDone
  const [swipeX, setSwipeX] = useState(0)
  const [isSwiping, setIsSwiping] = useState(false)
  // Completing slides the row off (Everyday), then collapses it; the task is marked done at the end.
  const [leaving, setLeaving] = useState<null | 'out' | 'collapse'>(null)
  const [leaveHeight, setLeaveHeight] = useState<number>()
  const touchStart = useRef<{ x: number; y: number } | null>(null)
  // Once a drag is decided horizontal or vertical it sticks, so scrolling never swipes.
  const swipeAxis = useRef<'none' | 'h' | 'v'>('none')
  const swipeable = !isDone && !leaving && (canTick || canMustDo)
  function handleTouchStart(e: React.TouchEvent) {
    if (!swipeable) return
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
    swipeAxis.current = 'none'
    setIsSwiping(true)
  }
  function handleTouchMove(e: React.TouchEvent) {
    if (!touchStart.current) return
    const dx = e.touches[0].clientX - touchStart.current.x
    const dy = e.touches[0].clientY - touchStart.current.y
    if (swipeAxis.current === 'none') {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
      swipeAxis.current = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v'
    }
    if (swipeAxis.current === 'v') return
    if ((dx > 0 && canTick) || (dx < 0 && canMustDo)) setSwipeX(dx)
  }
  function handleTouchEnd() {
    if (!touchStart.current) return
    touchStart.current = null
    setIsSwiping(false)
    if (swipeX >= SWIPE_THRESHOLD) {
      haptic('medium')
      // Celebrate once in handleComplete — don't double here, since swipe calls
      // handleComplete after the hold delay.
      completeOut()
      return
    } else if (swipeX <= -SWIPE_THRESHOLD) {
      haptic('medium')
      void mustDo(task._id, task.mustDoOn != null ? null : 'today')
    }
    setSwipeX(0)
  }
  const swipeProgress = Math.min(Math.max(swipeX, 0) / SWIPE_THRESHOLD, 1)
  const mustProgress = Math.min(Math.max(-swipeX, 0) / SWIPE_THRESHOLD, 1)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressTriggered = useRef(false)

  function handleCheckboxPointerDown(e: React.PointerEvent) {
    if (!canTick) return
    e.stopPropagation()
    longPressTriggered.current = false
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true
      // Long-press a done task to un-complete it straight into in-progress.
      if (isDone) {
        setOptimisticDone(false)
        setLocalStatus('in-progress')
        if (online) {
          uncomplete({ taskId: task._id }).catch(() => setOptimisticDone(null))
          setTaskStatus({ taskId: task._id, status: 'in-progress' }).catch(() =>
            setLocalStatus('backlog'),
          )
        } else {
          enqueueOp({ kind: 'uncomplete', args: { taskId: task._id as string } })
          enqueueOp({
            kind: 'setTaskStatus',
            args: { taskId: task._id as string, status: 'in-progress' },
          })
        }
        return
      }
      const next = localStatus === 'in-progress' ? 'backlog' : 'in-progress'
      setLocalStatus(next)
      if (online) {
        setTaskStatus({ taskId: task._id, status: next }).catch(() => setLocalStatus(localStatus))
      } else {
        enqueueOp({ kind: 'setTaskStatus', args: { taskId: task._id as string, status: next } })
      }
    }, 500)
  }

  function handleCheckboxPointerUp() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  function completeOut(celebrate = true) {
    if (celebrate) celebrateFrom(rowRef.current)
    playDoneSound()
    setLeaveHeight(rowRef.current?.offsetHeight)
    setLeaving('out')
    setTimeout(() => setLeaving('collapse'), LEAVE_SLIDE_MS)
    setTimeout(
      () => void handleComplete({ celebrate: false, sound: false }),
      LEAVE_SLIDE_MS + LEAVE_COLLAPSE_MS,
    )
  }
  // The row comes back once the server has it done (shown when nothing folds it away) or on undo.
  useEffect(() => {
    if (task.completedAt !== null || optimisticDone === false) {
      setLeaving(null)
      setTicked(false)
      setSwipeX(0)
    }
  }, [task.completedAt, optimisticDone])

  // Like Everyday: the circle fills at once, then the row sweeps; a second tap meanwhile cancels.
  const tickTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [ticked, setTicked] = useState(false)
  function tapComplete(from: Element) {
    if (tickTimer.current) {
      clearTimeout(tickTimer.current)
      tickTimer.current = null
      setTicked(false)
      return
    }
    haptic('medium')
    celebrateFrom(from)
    setTicked(true)
    tickTimer.current = setTimeout(() => {
      tickTimer.current = null
      completeOut(false)
    }, TAP_HOLD_MS)
  }
  useEffect(() => () => clearTimeout(tickTimer.current ?? undefined), [])

  async function handleComplete({ celebrate = true, sound = true } = {}) {
    if (isDone) return
    setOptimisticDone(true)
    if (sound) playDoneSound()
    if (celebrate) celebrateFrom(rowRef.current)
    // Undo talks to the server directly: handleUncomplete's isDone guard is stale here.
    undoToast(t('today.markedDone', { defaultValue: 'Marked done' }), () => {
      setOptimisticDone(false)
      if (!online) enqueueOp({ kind: 'uncomplete', args: { taskId: task._id as string } })
      else uncomplete({ taskId: task._id }).catch(() => setOptimisticDone(null))
    })
    if (!online) {
      enqueueOp({ kind: 'complete', args: { taskId: task._id as string } })
      return
    }
    try {
      await complete({ taskId: task._id })
      goalCtx?.onTickWhileFrozen?.()
    } catch {
      setOptimisticDone(null)
    }
  }

  async function handleUncomplete() {
    if (!isDone) return
    setOptimisticDone(false)
    if (!online) {
      enqueueOp({ kind: 'uncomplete', args: { taskId: task._id as string } })
      return
    }
    try {
      await uncomplete({ taskId: task._id })
    } catch {
      setOptimisticDone(null)
    }
  }

  function handleView() {
    goalCtx?.onView({
      nodeId: task._id,
      type: 'task',
      title: task.title,
      description: task.description,
      category: goalCtx.category,
      goalTitle: goalCtx.goalTitle,
      milestoneTitle,
      targetTitle,
      effort: task.effort,
      completedAt: task.completedAt,
      status: task.status,
      dueDate: task.dueDate,
      recurs: task.recurs,
      recursPeriod: task.recursPeriod,
      recursDays: task.recursDays,
      recurrenceDay: task.recurrenceDay,
      recurrenceTime: task.recurrenceTime,
      durationMinutes: task.durationMinutes,
      allDay: task.allDay,
    })
  }

  const hasDue = !!(task.dueDate && !task.recursPeriod && !task.recurs)
  const hasRecurs = !!(task.recursPeriod || task.recurs)
  const overdue = hasDue && !isDone && task.dueDate! < Date.now()

  return (
    <>
      {ctxMenu.menu && (
        <GoalTaskMenu
          pos={ctxMenu.menu}
          onClose={ctxMenu.closeMenu}
          task={task}
          isDone={isDone}
          isInProgress={isInProgress}
          isOwner={!!goalCtx?.isOwner}
          viewerIsCoach={!!goalCtx?.viewerIsCoach}
          onToggleDone={isDone ? handleUncomplete : () => completeOut()}
          onDetails={handleView}
          onRename={
            isTouch ? undefined : () => setTimeout(() => taskTitleRef.current?.startEdit(), 50)
          }
          onDelete={() => setPendingDelete(true)}
        />
      )}
      <DeleteTaskDialog
        task={pendingDelete ? task : null}
        onClose={() => setPendingDelete(false)}
      />
      <li
        ref={rowRef}
        id={anchor ? `goal-node-${task._id}` : undefined}
        data-swipe-x={swipeable ? '' : undefined}
        className={cn(
          'group/task relative cursor-pointer overflow-hidden border-b border-border/30 last:border-b-0',
          highlighted && 'task-highlight',
        )}
        style={
          leaving
            ? {
                maxHeight: leaving === 'collapse' ? 0 : leaveHeight,
                transition: `max-height ${LEAVE_COLLAPSE_MS}ms ease`,
              }
            : undefined
        }
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        {...(canEdit ? ctxMenu.longPressHandlers : {})}
        onClick={(e) => {
          if (ctxMenu.suppressClick(e)) return
          handleView()
        }}
      >
        {canMustDo && (
          <div
            aria-hidden
            style={{
              opacity: mustProgress,
              transition: isSwiping ? 'none' : 'opacity 200ms ease-out',
            }}
            className="pointer-events-none absolute inset-0 flex items-center justify-end gap-1.5 bg-primary pr-4 text-[12px] font-bold text-primary-foreground select-none"
          >
            <CalendarCheck className="h-4 w-4" strokeWidth={2.5} />
            {t(task.mustDoOn != null ? 'mustDo.remove' : 'mustDo.doToday')}
          </div>
        )}
        {canTick && (
          <div
            aria-hidden
            style={{
              opacity: leaving ? 1 : swipeProgress,
              transition: isSwiping ? 'none' : 'opacity 200ms ease-out',
            }}
            className="pointer-events-none absolute inset-0 flex items-center bg-primary pl-4"
          >
            <Check
              className="h-4 w-4 text-white dark:text-[#0a0a0a]"
              strokeWidth={3}
              style={{
                transform: `scale(${leaving ? 1 : swipeProgress * 0.65 + 0.35})`,
                transition: isSwiping
                  ? 'none'
                  : 'transform 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              }}
            />
          </div>
        )}
        <div
          style={{
            transform: leaving ? 'translateX(110%)' : `translateX(${swipeX}px)`,
            transition: isSwiping
              ? 'none'
              : leaving
                ? `transform ${LEAVE_SLIDE_MS}ms ease`
                : 'transform 200ms ease-out',
          }}
          className={cn(
            'relative flex items-center gap-3 py-1.5 select-none [-webkit-touch-callout:none] [@media(pointer:fine)]:select-auto',
            // Up next's card has no side padding, so the swipe colour fills it edge to edge.
            !anchor && 'px-4',
          )}
        >
          <button
            id={anchor ? `task-checkbox-${task._id}` : undefined}
            type="button"
            onPointerDown={canTick ? handleCheckboxPointerDown : undefined}
            onPointerUp={handleCheckboxPointerUp}
            onPointerLeave={handleCheckboxPointerUp}
            onPointerCancel={handleCheckboxPointerUp}
            onClick={
              canTick
                ? (e) => {
                    e.stopPropagation()
                    if (longPressTriggered.current) {
                      longPressTriggered.current = false
                      return
                    }
                    if (isDone) handleUncomplete()
                    else tapComplete(e.currentTarget)
                  }
                : undefined
            }
            disabled={!canTick}
            aria-label={
              isDone
                ? t('common.undoComplete')
                : isInProgress
                  ? t('goals.inProgress')
                  : 'Mark complete'
            }
            title={
              isDone ? t('common.undoComplete') : isInProgress ? t('goals.inProgress') : undefined
            }
            className={cn('shrink-0 text-foreground', canTick && 'cursor-pointer')}
          >
            <TaskStatusIcon
              status={isDone || ticked ? 'done' : isInProgress ? 'in_progress' : 'pending'}
              size={18}
            />
          </button>
          <div className="min-w-0 flex-1">
            <InlineEditor
              ref={taskTitleRef}
              value={task.title}
              onSave={(t) => rename(task._id, t)}
              disabled={!canEdit || isTouch}
              className={cn(
                'block text-sm font-medium',
                !isDone && 'truncate',
                isDone && 'text-muted-foreground/60 line-through',
              )}
            />
            {task.description && (
              <p className="line-clamp-2 text-xs text-muted-foreground [overflow-wrap:anywhere]">
                <LinkifiedText text={task.description} />
              </p>
            )}
            {context && (
              <p className="truncate font-mono text-[11px] text-muted-foreground/60">{context}</p>
            )}
            {task.lastEditedBy && (
              <CoachEditedTag by={task.lastEditedBy} viewerIsCoach={goalCtx?.viewerIsCoach} />
            )}
          </div>
          {startHere && !isDone && (
            <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold tracking-wide text-primary-foreground uppercase">
              {t('today.startHere')}
            </span>
          )}
          <div className="flex shrink-0 items-center gap-2.5 font-mono text-[11px] text-muted-foreground/60">
            {task.effort === 'high' && !isDone && (
              <UrgentFlag
                superUrgent={task.superUrgent}
                title={task.superUrgent ? undefined : t('filter.priorityUrgent')}
              />
            )}
            {hasDue && (
              <span
                className={cn(
                  'flex items-center gap-1',
                  overdue && 'text-red-500 dark:text-red-400',
                )}
              >
                <CalendarDays className="h-2.5 w-2.5 shrink-0" />
                {overdue && `${t('goals.overdue', { defaultValue: 'Overdue' })} · `}
                {new Date(task.dueDate!).toLocaleDateString(i18next.language, {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            )}
            {hasRecurs && (
              <span className="hidden items-center gap-1 sm:flex">
                <RefreshCw className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} />
                {periodLabel(t, i18next.language, task.recursPeriod, task.recursDays)}
              </span>
            )}
            <span>{taskTimeLabel(task, '')}</span>
          </div>
        </div>
      </li>
    </>
  )
}

function TargetSection({
  target,
  showLabel,
  rename,
  addNode,
  deleteNode,
  category,
  milestoneTitle,
  canEdit = true,
}: {
  target: TargetNode
  // Targets are an optional grouping: a milestone with a single target shows its
  // tasks directly, without the label.
  showLabel: boolean
  rename: RenameHandler
  addNode: AddHandler
  deleteNode: DeleteHandler
  category: string
  milestoneTitle: string
  canEdit?: boolean
}) {
  const { t } = useTranslation()
  const goalCtx = useContext(GoalCtx)
  const setTaskStatus = useSetTaskStatus(goalCtx ? { goalId: goalCtx.goalId } : undefined)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const headerRef = useRef<HTMLDivElement>(null)
  const targetTitleRef = useRef<InlineEditorHandle>(null)
  const isTouch = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
  const { menuOpen, menuAnchor, openAt, longPressHandlers, handleOpenChange, longPressOccurred } =
    useContextMenu()
  const focus = useContext(FocusNodeContext)
  const focusNodeId = focus.id
  const [highlighted, setHighlighted] = useState(false)
  useEffect(() => {
    if (focusNodeId !== target._id) return
    // Start once the smooth scroll has landed; clear after the glow fades.
    setHighlighted(false)
    const t = setTimeout(() => setHighlighted(true), 700)
    const u = setTimeout(() => setHighlighted(false), 3600)
    return () => {
      clearTimeout(t)
      clearTimeout(u)
    }
  }, [focusNodeId, focus.n, target._id])

  const incomplete = target.tasks.filter((t) => t.completedAt === null)
  const allInProgress = incomplete.length > 0 && incomplete.every((t) => t.status === 'in-progress')
  const doneTasks = target.tasks.filter((t) => t.completedAt !== null)
  const complete = target.tasks.length > 0 && incomplete.length === 0
  // Finished targets collapse to their header; finished tasks in a partly done
  // target fold into a "N done" row so the open ones lead.
  const [open, setOpen] = useState(!complete)
  const [showDone, setShowDone] = useState(false)
  const [adding, setAdding] = useState(false)
  const wasComplete = useRef(complete)
  useEffect(() => {
    if (complete && !wasComplete.current) setOpen(false)
    wasComplete.current = complete
  }, [complete])
  useEffect(() => {
    if (!focusNodeId || !target.tasks.some((tk) => tk._id === focusNodeId)) return
    setOpen(true)
    if (doneTasks.some((tk) => tk._id === focusNodeId)) setShowDone(true)
  }, [focusNodeId, focus.n]) // eslint-disable-line react-hooks/exhaustive-deps
  const expanded = !showLabel || open
  const stuck = useStuck(headerRef, showLabel)
  const foldDone = !showDone && doneTasks.length > 0 && incomplete.length > 0
  const visibleTasks = foldDone ? incomplete : target.tasks

  function handleView() {
    goalCtx?.onView({
      nodeId: target._id,
      type: 'target',
      title: target.title,
      category,
      goalTitle: goalCtx.goalTitle,
      milestoneTitle,
    })
  }

  async function handleDelete() {
    try {
      await deleteNode(target._id)
    } catch {
      toast.error("Couldn't delete — please try again.")
    }
  }

  function toggleInProgress() {
    const next = allInProgress ? 'backlog' : 'in-progress'
    const toUpdate = allInProgress
      ? incomplete
      : incomplete.filter((t) => t.status !== 'in-progress')
    Promise.all(toUpdate.map((t) => setTaskStatus({ taskId: t._id, status: next }))).catch(() => {})
  }

  return (
    <>
      <Popover open={menuOpen} onOpenChange={handleOpenChange}>
        <div>
          {showLabel ? (
            <div
              ref={headerRef}
              id={`goal-node-${target._id}`}
              className={cn(
                'sticky top-[calc(var(--app-hdr-h)+var(--goal-bar-h,0px)+var(--ms-hdr-h,0px))] z-10 group/target -mx-2 mt-2 flex cursor-pointer items-center gap-2 rounded-md border-b border-transparent bg-background px-2 py-1.5 transition-colors hover:bg-[color-mix(in_oklab,var(--muted)_40%,var(--background))]',
                stuck && 'rounded-none border-border/60',
                highlighted && 'task-highlight',
              )}
              {...(canEdit ? longPressHandlers : {})}
              onContextMenu={(e) => {
                e.preventDefault()
                openAt(e.clientX, e.clientY)
              }}
              onClick={() => {
                if (longPressOccurred.current) {
                  longPressOccurred.current = false
                  return
                }
                setOpen((v) => !v)
              }}
            >
              {menuAnchor && (
                <div
                  style={{
                    position: 'fixed',
                    left: menuAnchor.x,
                    top: menuAnchor.y,
                    width: 0,
                    height: 0,
                    pointerEvents: 'none',
                  }}
                >
                  <PopoverTrigger
                    style={{ width: 0, height: 0, overflow: 'hidden', display: 'block' }}
                    aria-hidden
                  />
                </div>
              )}
              <ChevronRight
                className={cn(
                  'size-3.5 shrink-0 text-muted-foreground/50 transition-transform',
                  open && 'rotate-90',
                )}
              />
              {complete && (
                <Check className="size-3.5 shrink-0 text-primary-text" strokeWidth={3} />
              )}
              <div className="min-w-0 flex-1">
                <InlineEditor
                  ref={targetTitleRef}
                  value={target.title}
                  onSave={(t) => rename(target._id, t)}
                  disabled={!canEdit || isTouch}
                  className={cn(
                    'block truncate text-[11px] font-bold uppercase tracking-widest',
                    complete ? 'text-muted-foreground/60' : 'text-muted-foreground',
                  )}
                />
              </div>
              <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground/60">
                {doneTasks.length}/{target.tasks.length}
              </span>
              {canEdit && (
                <button
                  type="button"
                  aria-label={t('goals.addTask')}
                  title={t('goals.addTask')}
                  onClick={(e) => {
                    e.stopPropagation()
                    setOpen(true)
                    setAdding(true)
                  }}
                  className="flex size-5.5 shrink-0 cursor-pointer items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover/target:opacity-100 focus-visible:opacity-100 [@media(pointer:coarse)]:opacity-100"
                >
                  <Plus className="size-3.5" />
                </button>
              )}
            </div>
          ) : (
            <span id={`goal-node-${target._id}`} aria-hidden />
          )}
          {expanded && (
            <>
              {doneTasks.length > 0 && incomplete.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowDone((v) => !v)}
                  className="flex w-full cursor-pointer items-center gap-2 border-b border-border/30 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Check className="size-3.5 text-primary-text" strokeWidth={3} />
                  {showDone
                    ? t('goals.hideDone')
                    : t('goals.doneCount', { count: doneTasks.length })}
                </button>
              )}
              <ul>
                {visibleTasks.map((task) => (
                  <TaskRow
                    key={task._id}
                    task={task}
                    rename={rename}
                    milestoneTitle={milestoneTitle}
                    targetTitle={target.title}
                    canEdit={canEdit}
                  />
                ))}
              </ul>
              {canEdit &&
                (showLabel ? (
                  adding && (
                    <AddNodeRow
                      defaultOpen
                      onClose={() => setAdding(false)}
                      label={t('goals.addTask')}
                      onAdd={(t) =>
                        addNode({ parentId: target._id, type: 'task', title: t, category })
                      }
                    />
                  )
                ) : (
                  <div className="opacity-0 transition-opacity focus-within:opacity-100 group-hover/ms:opacity-100 [@media(pointer:coarse)]:opacity-100">
                    <AddNodeRow
                      label={t('goals.addTask')}
                      onAdd={(t) =>
                        addNode({ parentId: target._id, type: 'task', title: t, category })
                      }
                    />
                  </div>
                ))}
            </>
          )}
        </div>
        <PopoverContent
          className="w-auto min-w-[160px] !p-1 !gap-0 [&_button]:focus-visible:outline-none"
          align="end"
          side="bottom"
        >
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
            onClick={() => {
              handleOpenChange(false)
              handleView()
            }}
          >
            <Info className="h-3.5 w-3.5" />
            {t('common.details')}
          </button>
          {incomplete.length > 0 && (
            <button
              type="button"
              className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
              onClick={() => {
                handleOpenChange(false)
                toggleInProgress()
              }}
            >
              <LoaderCircle className={cn('h-3.5 w-3.5', allInProgress && 'text-primary-text')} />
              {t('goals.inProgress')}
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
              onClick={() => {
                handleOpenChange(false)
                setTimeout(() => targetTitleRef.current?.startEdit(), 50)
              }}
            >
              <PencilLine className="h-3.5 w-3.5" />
              {t('common.rename')}
            </button>
          )}
          <div className="my-1 h-px bg-border" />
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
            onClick={() => {
              handleOpenChange(false)
              setDeleteOpen(true)
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t('common.delete')}
          </button>
        </PopoverContent>
      </Popover>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('common.deleteNodeTitle', { label: target.title })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('common.deleteNodeDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDelete}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

const parentOf = (el: HTMLElement) => el.parentElement

function MilestoneSection({
  milestone,
  msIdx,
  msCount,
  state,
  folded,
  onFold,
  onUnfold,
  rename,
  addNode,
  deleteNode,
  category,
  canEdit = true,
}: {
  milestone: MilestoneNode
  msIdx: number
  msCount: number
  state: MilestoneState
  // Finished milestones fold to one line automatically; the user can reopen one.
  folded: boolean
  onFold: () => void
  onUnfold: () => void
  rename: RenameHandler
  addNode: AddHandler
  deleteNode: DeleteHandler
  category: string
  canEdit?: boolean
}) {
  const { t } = useTranslation()
  const goalCtx = useContext(GoalCtx)
  const setTaskStatus = useSetTaskStatus(goalCtx ? { goalId: goalCtx.goalId } : undefined)
  const allTasks = milestone.targets.flatMap((tg) => tg.tasks)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const headerRef = useRef<HTMLDivElement>(null)
  const msTitleRef = useRef<InlineEditorHandle>(null)
  const isTouch = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
  const { menuOpen, menuAnchor, longPressHandlers, handleOpenChange, longPressOccurred } =
    useContextMenu()
  const focus = useContext(FocusNodeContext)
  const focusNodeId = focus.id
  const [highlighted, setHighlighted] = useState(false)
  useEffect(() => {
    if (focusNodeId !== milestone._id) return
    // Start once the smooth scroll has landed; clear after the glow fades.
    setHighlighted(false)
    const t = setTimeout(() => setHighlighted(true), 700)
    const u = setTimeout(() => setHighlighted(false), 3600)
    return () => {
      clearTimeout(t)
      clearTimeout(u)
    }
  }, [focusNodeId, focus.n, milestone._id])

  const incomplete = allTasks.filter((t) => t.completedAt === null)
  const allInProgress = incomplete.length > 0 && incomplete.every((t) => t.status === 'in-progress')

  function handleView() {
    goalCtx?.onView({
      nodeId: milestone._id,
      type: 'milestone',
      title: milestone.title,
      category,
      goalTitle: goalCtx.goalTitle,
    })
  }

  async function handleDelete() {
    try {
      await deleteNode(milestone._id)
    } catch {
      toast.error("Couldn't delete — please try again.")
    }
  }

  function toggleInProgress() {
    const next = allInProgress ? 'backlog' : 'in-progress'
    const toUpdate = allInProgress
      ? incomplete
      : incomplete.filter((t) => t.status !== 'in-progress')
    Promise.all(toUpdate.map((t) => setTaskStatus({ taskId: t._id, status: next }))).catch(() => {})
  }

  const doneCount = allTasks.length - incomplete.length
  useStickyHeightVar(headerRef, '--ms-hdr-h', parentOf, !folded)
  const stuck = useStuck(headerRef, !folded)

  if (folded && state === 'upcoming') {
    return (
      <button
        type="button"
        id={`goal-node-${milestone._id}`}
        onClick={onUnfold}
        className={cn(
          'grid w-full cursor-pointer gap-1.5 rounded-xl border border-border/60 px-4 py-3 text-left transition-colors hover:border-border hover:bg-muted/30',
          highlighted && 'task-highlight',
        )}
      >
        <span className="flex items-center justify-between gap-2">
          <MilestoneEyebrow index={msIdx} count={msCount} />
          <MilestonePill state={state} />
        </span>
        <span className="truncate text-[15px] font-extrabold text-muted-foreground">
          {milestone.title}
        </span>
        {allTasks.length > 0 && <MilestoneProgress done={doneCount} total={allTasks.length} />}
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground/60">
          <ChevronRight className="size-3.5" />
          {[
            milestone.targets.length > 1
              ? t('goals.targetTotal', { count: milestone.targets.length })
              : null,
            t('goals.taskTotal', { count: allTasks.length }),
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </button>
    )
  }

  if (folded) {
    return (
      <FoldedMilestone
        id={`goal-node-${milestone._id}`}
        index={msIdx}
        count={msCount}
        title={milestone.title}
        done={doneCount}
        total={allTasks.length}
        onClick={onUnfold}
        className={cn(highlighted && 'task-highlight')}
      />
    )
  }

  return (
    <>
      <Popover open={menuOpen} onOpenChange={handleOpenChange}>
        <section id={`goal-node-${milestone._id}`} className="group/ms scroll-mt-28">
          <div
            ref={headerRef}
            className={cn(
              'sticky top-[calc(var(--app-hdr-h)+var(--goal-bar-h,0px))] z-20 -mx-4 grid cursor-pointer gap-1.5 border-b border-transparent bg-background px-4 py-2',
              stuck && 'border-border/60',
              highlighted && 'task-highlight',
            )}
            {...(canEdit ? longPressHandlers : {})}
            onClick={() => {
              if (longPressOccurred.current) {
                longPressOccurred.current = false
                return
              }
              handleView()
            }}
          >
            {menuAnchor && (
              <div
                style={{
                  position: 'fixed',
                  left: menuAnchor.x,
                  top: menuAnchor.y,
                  width: 0,
                  height: 0,
                  pointerEvents: 'none',
                }}
              >
                <PopoverTrigger
                  style={{ width: 0, height: 0, overflow: 'hidden', display: 'block' }}
                  aria-hidden
                />
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              <MilestoneEyebrow index={msIdx} count={msCount} />
              <MilestonePill state={state} />
            </div>
            <InlineEditor
              ref={msTitleRef}
              value={milestone.title}
              onSave={(t) => rename(milestone._id, t)}
              disabled={!canEdit || isTouch}
              className={cn(
                'text-lg font-extrabold leading-snug',
                state === 'upcoming' && 'text-muted-foreground',
              )}
            />
            {allTasks.length > 0 && <MilestoneProgress done={doneCount} total={allTasks.length} />}
          </div>
          <div className="mt-1">
            {milestone.targets.map((target) => (
              <TargetSection
                key={target._id}
                target={target}
                showLabel={milestone.targets.length > 1}
                rename={rename}
                addNode={addNode}
                deleteNode={deleteNode}
                category={category}
                milestoneTitle={milestone.title}
                canEdit={canEdit}
              />
            ))}
          </div>
          {canEdit && (
            <div className="mt-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover/ms:opacity-100 [@media(pointer:coarse)]:opacity-100">
              <AddNodeRow
                label={t('goals.addTarget')}
                onAdd={(title) =>
                  addNode({ parentId: milestone._id, type: 'target', title, category })
                }
              />
            </div>
          )}
          {state !== 'current' && (
            <button
              type="button"
              onClick={onFold}
              className="mt-2 cursor-pointer text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('goals.foldMilestone', { defaultValue: 'Fold milestone' })}
            </button>
          )}
        </section>
        <PopoverContent
          className="w-auto min-w-[160px] !p-1 !gap-0 [&_button]:focus-visible:outline-none"
          align="end"
          side="bottom"
        >
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
            onClick={() => {
              handleOpenChange(false)
              handleView()
            }}
          >
            <Info className="h-3.5 w-3.5" />
            {t('common.details')}
          </button>
          {incomplete.length > 0 && (
            <button
              type="button"
              className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
              onClick={() => {
                handleOpenChange(false)
                toggleInProgress()
              }}
            >
              <LoaderCircle className={cn('h-3.5 w-3.5', allInProgress && 'text-primary-text')} />
              {t('goals.inProgress')}
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
              onClick={() => {
                handleOpenChange(false)
                setTimeout(() => msTitleRef.current?.startEdit(), 50)
              }}
            >
              <PencilLine className="h-3.5 w-3.5" />
              {t('common.rename')}
            </button>
          )}
          <div className="my-1 h-px bg-border" />
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
            onClick={() => {
              handleOpenChange(false)
              setDeleteOpen(true)
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t('common.delete')}
          </button>
        </PopoverContent>
      </Popover>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('common.deleteNodeTitle', { label: milestone.title })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('common.deleteNodeDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDelete}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default function GoalTree({
  tree,
  canEdit = true,
  viewerIsCoach,
  openMilestoneId,
  focusTaskId,
  focusNodeId,
  onAccordionChange,
}: {
  tree: GoalTreeData
  canEdit?: boolean
  viewerIsCoach?: boolean
  openMilestoneId?: string
  focusTaskId?: string
  focusNodeId?: string
  // Fired when the user folds/unfolds a milestone (the route clears ?milestone=).
  onAccordionChange?: () => void
}) {
  const { t } = useTranslation()
  const addNode = useAddNode()
  const deleteNode = useDeleteNode()
  const rename = useRenameNode()
  const replanStatus = useGoalReplanStatus(tree._id)
  const goals = useGoals()
  const { frozen } = goalFreezeState(goals?.find((g) => g._id === tree._id)?.freezes)
  const { unfreeze } = useGoalFreezeActions(tree._id)
  const [selectedNode, setSelectedNode] = useState<NodeDetail | null>(null)
  // Only the current milestone is open by default; finished and upcoming ones
  // fold until the user opens them (remembered per goal on this device).
  const openKey = `goal-open-milestones:${tree._id}`
  const [reopened, setReopened] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(openKey) ?? '[]') as string[])
    } catch {
      return new Set()
    }
  })

  // "Done" from task completion directly — ms.completedAt / ms.progress can be stale.
  const msDone = (ms: MilestoneNode) => {
    const tasks = ms.targets.flatMap((tg) => tg.tasks)
    return tasks.length > 0 && tasks.every((t) => t.completedAt !== null)
  }
  const currentIdx = tree.milestones.findIndex((ms) => !msDone(ms))
  const stateOf = (ms: MilestoneNode, i: number): MilestoneState =>
    msDone(ms) ? 'done' : i === currentIdx ? 'current' : 'upcoming'

  const setOpen = (id: string, open: boolean) => {
    setReopened((prev) => {
      const next = new Set(prev)
      if (open) next.add(id)
      else next.delete(id)
      try {
        localStorage.setItem(openKey, JSON.stringify([...next]))
      } catch {}
      return next
    })
  }

  // Finishing a milestone folds it and brings the next one into view.
  const prevMsDone = useRef<boolean[]>(tree.milestones.map(msDone))
  useEffect(() => {
    const nowDone = tree.milestones.map(msDone)
    const justCompleted = nowDone.findIndex((done, i) => done && !prevMsDone.current[i])
    if (justCompleted !== -1) {
      setOpen(tree.milestones[justCompleted]._id, false)
      const nextMs = tree.milestones.slice(justCompleted + 1).find((ms) => !msDone(ms))
      if (nextMs) {
        setTimeout(() => {
          document
            .getElementById(`goal-node-${nextMs._id}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 300)
      }
    }
    prevMsDone.current = nowDone
  }, [tree.milestones]) // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link to a milestone (?milestone=): unfold it if finished, then scroll.
  useEffect(() => {
    if (!openMilestoneId) return
    setOpen(openMilestoneId, true)
    // When also focusing a node, the focus effect below owns the scroll.
    if (!focusNodeId && !focusTaskId) {
      setTimeout(() => {
        document
          .getElementById(`goal-node-${openMilestoneId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 150)
    }
    if (focusTaskId) {
      setTimeout(() => {
        document.getElementById(`task-checkbox-${focusTaskId}`)?.focus()
      }, 350)
    }
  }, [openMilestoneId, focusTaskId, focusNodeId])

  // In-page jumps (e.g. from the goal side panel) — repeatable for the same node,
  // unlike the ?focusNode= link.
  const [jump, setJump] = useState<{ id: string; n: number } | null>(null)
  useEffect(() => {
    const onJump = (e: Event) => {
      const id = (e as CustomEvent<{ id: string }>).detail?.id
      if (id) setJump((prev) => ({ id, n: (prev?.n ?? 0) + 1 }))
    }
    window.addEventListener('goal-focus-node', onJump)
    return () => window.removeEventListener('goal-focus-node', onJump)
  }, [])
  // The route clears ?focusNode= as soon as the milestone opens, before the
  // collapsed target inside it mounts, so keep the last one for that mount.
  const routeFocus = focusNodeId ?? focusTaskId
  const [lastRouteFocus, setLastRouteFocus] = useState(routeFocus)
  useEffect(() => {
    if (routeFocus) setLastRouteFocus(routeFocus)
  }, [routeFocus])
  const effectiveFocusNodeId = jump?.id ?? routeFocus ?? lastRouteFocus
  const focusValue = useMemo(
    () => ({ id: effectiveFocusNodeId, n: jump?.n ?? 0 }),
    [effectiveFocusNodeId, jump?.n],
  )
  useEffect(() => {
    const id = effectiveFocusNodeId
    if (!id) return
    const owner = tree.milestones.find(
      (ms) =>
        ms._id === id ||
        ms.targets.some((tg) => tg._id === id || tg.tasks.some((tk) => tk._id === id)),
    )
    if (owner) setOpen(owner._id, true)
    setTimeout(() => {
      document
        .getElementById(`goal-node-${id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 300)
  }, [effectiveFocusNodeId, jump?.n]) // eslint-disable-line react-hooks/exhaustive-deps

  // The next few open tasks, in plan order.
  const upNext: { task: TaskNode; milestone: MilestoneNode; target: TargetNode }[] = []
  for (const milestone of tree.milestones)
    for (const target of milestone.targets)
      for (const task of target.tasks)
        if (task.completedAt === null && upNext.length < 3) upNext.push({ task, milestone, target })
  // Just-created goal: badge its first open step, as Today does.
  const startHereTaskId =
    startHereGoalId() === tree._id
      ? upNext.find(({ task }) => !task.recurs && !task.recursPeriod)?.task._id
      : undefined

  return (
    <GoalCtx.Provider
      value={{
        goalId: tree._id,
        goalTitle: tree.title,
        category: tree.category,
        onView: setSelectedNode,
        viewerIsCoach,
        isOwner: tree.isOwner,
        onTickWhileFrozen: frozen
          ? () =>
              toast(t('goalFreeze.tickedWhileFrozen'), {
                id: `frozen-tick-${tree._id}`,
                icon: <Snowflake className="size-4 text-sky-400" />,
                action: { label: t('goalFreeze.unfreeze'), onClick: () => void unfreeze() },
                cancel: { label: t('goalFreeze.keepFrozen'), onClick: () => {} },
              })
          : undefined,
      }}
    >
      <FocusNodeContext.Provider value={focusValue}>
        <StartHereContext.Provider value={startHereTaskId}>
          <div className="space-y-6">
            {replanStatus !== null &&
              replanStatus !== undefined &&
              replanStatus.context.length > 0 && (
                <details className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
                  <summary className="cursor-pointer font-medium text-destructive">
                    Why was this replanned?
                  </summary>
                  <ul className="mt-2 space-y-1 text-muted-foreground">
                    {replanStatus.context.map((item, i) => (
                      <li key={i}>
                        {item.taskTitle} — skipped {item.skippedCount}×
                      </li>
                    ))}
                  </ul>
                </details>
              )}

            {canEdit && upNext.length > 0 && (
              <section className="overflow-hidden rounded-2xl border border-primary/25 bg-primary/5 pt-3">
                <p className="mb-1 px-4 text-[10px] font-bold uppercase tracking-widest text-primary-text">
                  {t('goals.upNext', { defaultValue: 'Up next' })}
                </p>
                <ul>
                  {upNext.map(({ task, milestone, target }) => (
                    <TaskRow
                      key={task._id}
                      task={task}
                      rename={rename}
                      milestoneTitle={milestone.title}
                      targetTitle={target.title}
                      canEdit={canEdit}
                      anchor={false}
                      context={milestone.title}
                    />
                  ))}
                </ul>
              </section>
            )}

            {tree.milestones.map((milestone, msIdx) => {
              const state = stateOf(milestone, msIdx)
              return (
                <MilestoneSection
                  key={milestone._id}
                  milestone={milestone}
                  msIdx={msIdx}
                  msCount={tree.milestones.length}
                  state={state}
                  folded={state !== 'current' && !reopened.has(milestone._id)}
                  onFold={() => {
                    setOpen(milestone._id, false)
                    onAccordionChange?.()
                  }}
                  onUnfold={() => {
                    setOpen(milestone._id, true)
                    onAccordionChange?.()
                  }}
                  rename={rename}
                  addNode={addNode}
                  deleteNode={deleteNode}
                  category={tree.category}
                  canEdit={canEdit}
                />
              )
            })}

            {canEdit && (
              <AddNodeRow
                label={t('goals.addMilestone')}
                onAdd={(title) =>
                  addNode({
                    parentId: tree._id,
                    type: 'milestone',
                    title,
                    category: tree.category,
                  })
                }
              />
            )}
          </div>

          <NodeDetailSheet
            node={selectedNode}
            goalId={tree._id}
            viewerIsCoach={viewerIsCoach}
            onClose={() => setSelectedNode(null)}
          />
        </StartHereContext.Provider>
      </FocusNodeContext.Provider>
    </GoalCtx.Provider>
  )
}
